import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { css, keyframes } from '@emotion/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Fab, FormControl, InputLabel, MenuItem, Select, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import { Stack, Box, BoxProps, Chip } from '@mui/material';
import { Locale, Types, useTranslation } from '@scorenco/core';
import { format } from 'date-fns';
import { fr, de } from 'date-fns/locale';
import { T_KEYS } from '../../translations';
import { pickTextColorBasedOnBgColorSimple } from '../../utils';
import { Game } from '../Game';
import { Link } from '../Link';
import { GetMatchDocument, GetTournamentsTeamDocument } from './AllGamesList.query.generated';
const createScrollingTextStyle = (isOverflow, animationWidth) => {
  const marquee = keyframes`
    0% {
      transform: translateX(0%);
    }
    20% {
      transform: translateX(0%);
    }
    80% {
      transform: translateX(-${animationWidth + 7}px);
    }
    100% {
      transform: translateX(-${animationWidth + 7}px);
    }
  `;
  return css`
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    animation: ${isOverflow ? marquee : ''} 10s linear infinite;
  `;
};
type AllGamesListProps = BoxProps & {
  tournamentId: number;
  tournament?: Types.Tournaments_Tournament;
};
export const AllGamesList = ({
  tournamentId,
  tournament,
  ...rest
}: AllGamesListProps) => {
  const {
    t,
    locale
  } = useTranslation();
  const [showTerrain, setShowTerrain] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const matchQuery = useQuery(GetMatchDocument, {
    variables: {
      id: tournamentId
    },
    context: {
      role: 'anonymous'
    },
    skip: !tournamentId
  });
  const tournamentsTeamQuery = useQuery(GetTournamentsTeamDocument, {
    variables: {
      id: tournamentId
    },
    context: {
      role: 'anonymous'
    },
    skip: !tournamentId
  });
  const teams = tournamentsTeamQuery.data?.tournaments_team;
  const matchs = matchQuery.data?.tournaments_game;
  const places = matchQuery.data?.tournaments_place;
  const filteredMatchs = matchs?.filter(match => (match?.tournament_place?.name === showTerrain || !showTerrain) && (!selectedTeam || selectedTeam === match.home_engagement?.team?.id || selectedTeam === match.away_engagement?.team?.id));
  const groupedMatches = filteredMatchs?.reduce((acc, match) => {
    const date = new Date(match.date).toISOString().split('T')[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(match);
    return acc;
  }, {});
  const sortedDates = Object.keys(groupedMatches || []).sort((a, b) => new Date(a) - new Date(b));
  return <Box {...rest} data-sentry-element="Box" data-sentry-component="AllGamesList" data-sentry-source-file="AllGamesList.tsx">
      {new Date() >= new Date(tournament?.start_date) && new Date() <= new Date(tournament?.end_date) && <div css={css`
              position: fixed;
              bottom: 1rem;
              left: 50%;
              transform: translateX(-50%);
              z-index: 100000;
              pointer-events: auto;
            `}>
            <Fab variant="extended" size="small" color="primary" onClick={() => {
        document.getElementById('scroll-anchor')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }}>
              {t(T_KEYS.MATCH_NOW)}
            </Fab>
          </div>}

      <Stack sx={{
      p: 2,
      flexWrap: 'wrap',
      gap: 2
    }} direction="row" data-sentry-element="Stack" data-sentry-source-file="AllGamesList.tsx">
        <FormControl sx={{
        width: '100%',
        maxWidth: '15rem'
      }} size="small" data-sentry-element="FormControl" data-sentry-source-file="AllGamesList.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="AllGamesList.tsx">Filtrer les terrains</InputLabel>
          <Select label="Filtrer les terrains" value={showTerrain} onChange={e => setShowTerrain(e.target.value)} data-sentry-element="Select" data-sentry-source-file="AllGamesList.tsx">
            <MenuItem value={''} data-sentry-element="MenuItem" data-sentry-source-file="AllGamesList.tsx">{t(T_KEYS.TOURNAMENTS_EQUIPMENT)}</MenuItem>
            {places?.map(place => <MenuItem key={place.id} value={place.name}>
                {place.name}
              </MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{
        width: '100%',
        maxWidth: '15rem'
      }} size="small" data-sentry-element="FormControl" data-sentry-source-file="AllGamesList.tsx">
          <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="AllGamesList.tsx">Filtrer les équipes</InputLabel>
          <Select label="Filtrer les équipes" value={selectedTeam} onChange={e => setSelectedTeam(Number(e.target.value))} data-sentry-element="Select" data-sentry-source-file="AllGamesList.tsx">
            <MenuItem value={null} data-sentry-element="MenuItem" data-sentry-source-file="AllGamesList.tsx">{t(T_KEYS.TEAMS_ALL)}</MenuItem>
            {teams?.map(team => <MenuItem value={team.id} key={team.id}>
                {team?.name}
              </MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {sortedDates.map(date => {
      const matches = groupedMatches[date];
      return <Accordion key={date} defaultExpanded={sortedDates.filter(date => new Date(date).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)).length >= 1 ? new Date(date).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0) : true} sx={{
        mb: 2
      }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} css={theme => css`
                width: 100%;
                text-align: center;
                background-color: ${theme.vars.palette.primary.main};
                color: ${theme.vars.palette.primary.contrastText};
                border-bottom: 1px solid;
                border-color: ${theme.vars.palette.divider};
                padding: 0;
                font-size: 0.8rem;

                &:first-letter {
                  text-transform: uppercase;
                }

                .MuiAccordionSummary-expandIconWrapper {
                  color: white;
                }
              `}>
              <Typography sx={{
            textAlign: 'center',
            width: '100%'
          }}>
                {format(new Date(date), 'EEEE d MMMM', {
              locale: locale === Locale.de_CH ? de : fr
            })}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {matches.map((match, idx) => {
            const isLive = match?.status === 'live';
            const isLastMatchWithResult = matches?.slice(idx + 1)?.findIndex(m => m?.home_score !== null && m?.away_score !== null) === -1;
            const isAnchor = isLive || isLastMatchWithResult;
            return <Link id={isAnchor ? 'scroll-anchor' : undefined} key={match.id} css={theme => css`
                      padding: 0 1rem;
                      border: 1px solid #e2e8ed;
                      color: ${theme.vars.palette.text.primary};
                      background-color: ${theme.vars.palette.background.paper};
                      transition:
                        box-shadow 0.5s cubic-bezier(0.165, 0.84, 0.44, 1),
                        background-color 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
                      margin-top: -1px;
                    `} href={match?.url}>
                    <Game key={match.id} game={{
                game: match
              }} />
                    <div css={css`
                        text-align: center;
                        font-size: 0.75rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      `}>
                      <MarqueeChip match={match} />
                    </div>
                  </Link>;
          })}
            </AccordionDetails>
          </Accordion>;
    })}
    </Box>;
};
const MarqueeChip = ({
  match
}) => {
  const [isOverflow, setIsOverflow] = useState(false);
  const [animationWidth, setAnimationWidth] = useState(0);
  const chipRef = useRef(null);
  const spanRef = useRef(null);
  useEffect(() => {
    const chipElement = chipRef.current;
    const spanElement = spanRef.current;
    if (chipElement && spanElement) {
      const chipWidth = chipElement.clientWidth - 7;
      const textWidth = spanElement.scrollWidth;
      if (textWidth > chipWidth) {
        setIsOverflow(true);
        setAnimationWidth(textWidth - chipWidth);
      } else {
        setIsOverflow(false);
      }
    }
  }, []);
  return <Chip ref={chipRef} label={<span ref={spanRef} css={createScrollingTextStyle(isOverflow, animationWidth)}>
          {[match?.tournament_place?.name, match?.status !== 'to_come' && new Date(match?.date).toLocaleDateString([], {
      month: 'short',
      day: '2-digit'
    }), match?.status !== 'to_come' && new Date(match?.date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    }), match?.home_engagement?.phase?.rule?.challenge?.name, match?.home_engagement?.phase?.name].filter(Boolean).join(' - ')}
        </span>} size="small" sx={{
    backgroundColor: match?.home_engagement?.phase?.rule?.challenge?.color,
    color: pickTextColorBasedOnBgColorSimple(match?.home_engagement?.phase?.rule?.challenge?.color || '#ffffff', 'white', 'black'),
    mr: 1,
    fontSize: '0.7rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1'
  }} data-sentry-element="Chip" data-sentry-component="MarqueeChip" data-sentry-source-file="AllGamesList.tsx" />;
};